import React, { useState, useEffect, useContext } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, Select, Space, Popconfirm, message,
  Card, Typography, Row, Col, Tag
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';
import { AuthContext } from '@app/providers/firebase/AuthProvider';
import useFirebase from '@app/providers/firebase';
import { FlexColumn, FlexRow } from '@shared/ui/Containers/FlexContainer';
import theme from '@config/theme';
import { usePermissions } from '@shared/permissions/usePermissions';
import { includes } from 'lodash-es';
import { IExceptionCode } from './types';
import db from '@services/db';
import {
  saveExceptionCode,
  createExceptionCode,
  deleteExceptionCode as deleteExceptionCodeService,
} from '@services/db/operations/qualityAssurance';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const PageWrapper = styled(FlexColumn)`
  flex-grow: 1;
  height: 100%;
  width: 100%;
  padding: 24px;
  gap: 24px;
  
  @media ${theme.device.laptopL} {
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const HeaderCard = styled(Card)`
  .ant-card-body {
    padding: 16px 24px;
  }
`;

const ActionButton = styled(Button)`
  margin-left: 8px;
`;

const ExceptionCodeManager: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(['*', 'admin.reports', 'qa.edit']);

  const [exceptionCodes, setExceptionCodes] = useState<IExceptionCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCode, setEditingCode] = useState<IExceptionCode | null>(null);
  const [form] = Form.useForm();



  // Load exception codes from Firestore
  const loadExceptionCodes = async () => {
    setLoading(true);
    try {
      const { collection, getDocs, query, orderBy } = await import('firebase/firestore');
      const exceptionCodesRef = collection(db.firestore, db.qualityAssurance.exceptionCodes);
      const q = query(exceptionCodesRef, orderBy('Description', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const codes: IExceptionCode[] = [];
      querySnapshot.forEach((doc) => {
        codes.push({ id: doc.id, ...doc.data() } as IExceptionCode);
      });
      
      setExceptionCodes(codes);
    } catch (error) {
      console.error('Error loading exception codes:', error);
      message.error('Failed to load exception codes');
    } finally {
      setLoading(false);
    }
  };

  // Save exception code
  const handleSaveExceptionCode = async (values: Omit<IExceptionCode, 'id'>) => {
    try {
      if (editingCode) {
        await saveExceptionCode(editingCode.id, values);
        message.success('Exception code updated successfully');
      } else {
        await createExceptionCode(values);
        message.success('Exception code created successfully');
      }
      setModalVisible(false);
      setEditingCode(null);
      form.resetFields();
      loadExceptionCodes();
    } catch (error) {
      console.error('Error saving exception code:', error);
      message.error('Failed to save exception code');
    }
  };

  // Delete exception code
  const handleDeleteExceptionCode = async (id: string) => {
    try {
      await deleteExceptionCodeService(id);
      message.success('Exception code deleted successfully');
      loadExceptionCodes();
    } catch (error) {
      console.error('Error deleting exception code:', error);
      message.error('Failed to delete exception code');
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    form.validateFields().then((values) => {
      // Process discount data
      const discount = values.discountType && values.discountAmount 
        ? {
            Type: values.discountType,
            Amount: values.discountAmount
          }
        : null;

      const exceptionCodeData = {
        ExceptionCode: values.exceptionCode,
        Part: values.part,
        Description: values.description,
        Notes: values.notes || '',
        Quantity: values.quantity || 0,
        Discount: discount
      };

      handleSaveExceptionCode(exceptionCodeData);
    });
  };

  // Handle edit
  const handleEdit = (record: IExceptionCode) => {
    setEditingCode(record);
    form.setFieldsValue({
      exceptionCode: record.ExceptionCode,
      part: record.Part,
      description: record.Description,
      notes: record.Notes,
      quantity: record.Quantity,
      discountType: record.Discount?.Type || null,
      discountAmount: record.Discount?.Amount || null
    });
    setModalVisible(true);
  };

  // Handle new
  const handleNew = () => {
    setEditingCode(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Table columns
  const columns = [
    {
      title: 'Code',
      dataIndex: 'ExceptionCode',
      key: 'exceptionCode',
      width: '10%',
    },
    {
      title: 'Part',
      dataIndex: 'Part',
      key: 'part',
      width: '10%',
    },
    {
      title: 'Description',
      dataIndex: 'Description',
      key: 'description',
      width: '25%',
    },
    // {
    //   title: 'Notes',
    //   dataIndex: 'Notes',
    //   key: 'notes',
    //   width: '30%',
    //   ellipsis: true,
    // },
    // {
    //   title: 'Quantity',
    //   dataIndex: 'Quantity',
    //   key: 'quantity',
    //   width: '12%',
    //   align: 'center' as const,
    // },
    {
      title: 'Discount',
      key: 'discount',
      width: '20%',
      render: (record: IExceptionCode) => {
        if (!record.Discount) {
          return <Tag color="default">None</Tag>;
        }
        const { Type, Amount } = record.Discount;
        const displayAmount = Type === 'percent' ? `${Amount}%` : `$${Amount}`;
        return (
          <Tag color={Type === 'percent' ? 'blue' : 'green'}>
            {displayAmount} ({Type})
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      align: 'center' as const,
      render: (record: IExceptionCode) => (
        <Space>
          {canEdit && (
            <>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                size="small"
              />
              <Popconfirm
                title="Are you sure you want to delete this exception code?"
                onConfirm={() => handleDeleteExceptionCode(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                />
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    loadExceptionCodes();
  }, []);

  return (
    <PageWrapper>
      <HeaderCard>
        <FlexRow justify="space-between" align="center">
          <div>
            <Title level={2} style={{ margin: 0 }}>
              Exception Codes Management
            </Title>
            <Text type="secondary">
              Manage exception codes for quality assurance and non-conformance tracking
            </Text>
          </div>
          {canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleNew}
            >
              Add Exception Code
            </Button>
          )}
        </FlexRow>
      </HeaderCard>

      <Card>
        <Table
          columns={columns}
          dataSource={exceptionCodes}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} exception codes`,
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      <Modal
        title={editingCode ? 'Edit Exception Code' : 'Add Exception Code'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          setEditingCode(null);
          form.resetFields();
        }}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            quantity: 0,
            discountType: null,
            discountAmount: null
          }}
        >
          <Form.Item
            name="exceptionCode"
            label="Exception Code"
            rules={[
              { required: true, message: 'Please enter an exception code' },
            ]}
          >
            <Input placeholder="Enter exception code" />
          </Form.Item>
          <Form.Item
            name="part"
            label="Applies to:"
            rules={[
              { required: true, message: 'Please enter a part' },
            ]}
          >
            <Select placeholder="Select part" allowClear>
              <Option value="Neck">Neck</Option>
              <Option value="Body">Body</Option>
              <Option value="Any">Any</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: 'Please enter a description' },
              { max: 100, message: 'Description must be less than 100 characters' }
            ]}
          >
            <Input placeholder="Enter exception code description" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
            rules={[
              { max: 500, message: 'Notes must be less than 500 characters' }
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Enter additional notes (optional)"
            />
          </Form.Item>

          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="discountType"
                label="Discount Type"
              >
                <Select placeholder="Select discount type" allowClear>
                  <Option value="percent">Percentage</Option>
                  <Option value="flat">Flat Amount</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="discountAmount"
                label="Discount Amount"
                dependencies={['discountType']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const discountType = getFieldValue('discountType');
                      if (discountType && (!value || value <= 0)) {
                        return Promise.reject(new Error('Please enter a valid discount amount'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="Enter discount amount"
                  formatter={(value) => {
                    const discountType = form.getFieldValue('discountType');
                    if (discountType === 'percent') {
                      return `${value}%`;
                    }
                    return `$${value}`;
                  }}
                  parser={(value) => (value?.replace(/[%$]/g, '') || '0') as any}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </PageWrapper>
  );
};

export default ExceptionCodeManager;
